import { storage } from '@/firebase/init';
import { ref, uploadBytes, getDownloadURL, deleteObject, listAll } from 'firebase/storage';

/**
 * Upload a single image file to Firebase Storage
 * @param {File} file - The image file to upload
 * @param {string} userId - The user's UID
 * @param {string} orderId - The order/journey ID
 * @returns {Promise<string>} - The download URL of the uploaded image
 */
export async function uploadImage(file, userId, orderId) {
  if (!file || !userId || !orderId) {
    throw new Error('Missing required parameters for image upload');
  }

  // Create a unique filename with timestamp
  const timestamp = Date.now();
  const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
  const filePath = `user-uploads/${userId}/${orderId}/${timestamp}_${sanitizedFileName}`;
  
  const storageRef = ref(storage, filePath);
  
  try {
    // Upload the file
    const snapshot = await uploadBytes(storageRef, file);
    
    // Get the download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    return downloadURL;
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
}

/**
 * Upload multiple images to Firebase Storage
 * @param {File[]} files - Array of image files
 * @param {string} userId - The user's UID
 * @param {string} orderId - The order/journey ID
 * @returns {Promise<string[]>} - Array of download URLs
 */
export async function uploadImages(files, userId, orderId) {
  if (!Array.isArray(files) || files.length === 0) {
    return [];
  }

  try {
    const uploadPromises = files.map(file => uploadImage(file, userId, orderId));
    const urls = await Promise.all(uploadPromises);
    return urls;
  } catch (error) {
    console.error('Error uploading multiple images:', error);
    throw error;
  }
}

/**
 * Upload images and return both File objects and URLs for immediate display
 * @param {File[]} files - Array of image files
 * @param {string} userId - The user's UID
 * @param {string} orderId - The order/journey ID
 * @returns {Promise<Array<{file: File, url: string, storageUrl: string}>>}
 */
export async function uploadImagesWithPreview(files, userId, orderId) {
  if (!Array.isArray(files) || files.length === 0) {
    return [];
  }

  try {
    const results = await Promise.all(
      files.map(async (file) => {
        const storageUrl = await uploadImage(file, userId, orderId);
        // Create local preview URL for immediate display
        const previewUrl = URL.createObjectURL(file);
        return {
          file,
          previewUrl,
          storageUrl,
          name: file.name,
          size: file.size,
          type: file.type
        };
      })
    );
    return results;
  } catch (error) {
    console.error('Error uploading images with preview:', error);
    throw error;
  }
}

/**
 * Delete a single image from Firebase Storage by URL
 * @param {string} imageUrl - The download URL of the image to delete
 * @returns {Promise<void>}
 */
export async function deleteImageByUrl(imageUrl) {
  if (!imageUrl) return;
  
  try {
    // Extract the storage path from the URL
    // Firebase Storage URLs format: https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{path}?alt=media&token={token}
    const urlObj = new URL(imageUrl);
    const pathMatch = urlObj.pathname.match(/\/o\/(.+)/);
    
    if (pathMatch && pathMatch[1]) {
      const filePath = decodeURIComponent(pathMatch[1]);
      const storageRef = ref(storage, filePath);
      await deleteObject(storageRef);
      console.log('Image deleted:', filePath);
    }
  } catch (error) {
    console.error('Error deleting image by URL:', error);
    // Don't throw - continue even if some images fail to delete
  }
}

/**
 * Delete multiple images from Firebase Storage by URLs
 * @param {string[]} imageUrls - Array of download URLs
 * @returns {Promise<void>}
 */
export async function deleteImagesByUrls(imageUrls) {
  if (!Array.isArray(imageUrls) || imageUrls.length === 0) {
    return;
  }
  
  try {
    const deletePromises = imageUrls.map(url => deleteImageByUrl(url));
    await Promise.all(deletePromises);
    console.log(`Deleted ${imageUrls.length} images from storage`);
  } catch (error) {
    console.error('Error deleting multiple images:', error);
  }
}

/**
 * Delete all images for a specific journey/order
 * @param {string} userId - The user's UID
 * @param {string} orderId - The order/journey ID
 * @returns {Promise<void>}
 */
export async function deleteJourneyImages(userId, orderId) {
  if (!userId || !orderId) return;
  
  try {
    const folderPath = `user-uploads/${userId}/${orderId}`;
    const folderRef = ref(storage, folderPath);
    
    // List all files in the folder
    const listResult = await listAll(folderRef);
    
    // Delete all files
    const deletePromises = listResult.items.map(itemRef => deleteObject(itemRef));
    await Promise.all(deletePromises);
    
    console.log(`Deleted all images for journey ${orderId} (${listResult.items.length} files)`);
  } catch (error) {
    console.error('Error deleting journey images:', error);
    // Don't throw - continue even if deletion fails
  }
}

/**
 * Upload images for a specific item in a journey with sequential naming
 * 
 * Storage Path Structure:
 * =======================
 * user/{userId}/journey/{journeyId}/item/{itemName}/image-1.jpeg, image-2.jpeg, etc.
 * 
 * For Journey/Set orders:
 * - journeyId = productId (e.g., "621b7028fd494b93bb109624-1766233682253")
 * - itemName = sanitized product name (e.g., "Crystal_Carving", "Lightbox")
 * - Each product in the set has its OWN subfolder
 * 
 * Example paths:
 * - user/abc123/journey/621b7028-1234/item/Crystal_Carving/image-1.jpeg
 * - user/abc123/journey/621b7028-1234/item/Lightbox/image-1.jpeg
 * - user/abc123/journey/621b7028-1234/item/Cupbearer/image-1.jpeg
 * 
 * @param {File[]} files - Array of image files
 * @param {string} userId - The user's UID
 * @param {string} journeyId - The journey ID (productId for sets)
 * @param {string} itemName - The item/product name (e.g., "Crystal Carving")
 * @returns {Promise<string[]>} - Array of download URLs
 */
export async function uploadItemImages(files, userId, journeyId, itemName) {
  if (!Array.isArray(files) || files.length === 0) {
    return [];
  }

  if (!userId || !journeyId || !itemName) {
    throw new Error('Missing required parameters for item image upload');
  }

  try {
    // Sanitize item name for use in path
    const sanitizedItemName = itemName.replace(/[^a-zA-Z0-9-_]/g, '_');
    
    const uploadPromises = files.map(async (file, index) => {
      // Sequential naming: image-1, image-2, etc.
      const imageNumber = index + 1;
      const extension = file.name.split('.').pop() || 'jpg';
      const fileName = `image-${imageNumber}.${extension}`;
      const filePath = `user/${userId}/journey/${journeyId}/item/${sanitizedItemName}/${fileName}`;
      
      const storageRef = ref(storage, filePath);
      
      // Upload the file (this will overwrite if exists)
      const snapshot = await uploadBytes(storageRef, file);
      
      // Get the download URL
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return downloadURL;
    });

    const urls = await Promise.all(uploadPromises);
    console.log(`Uploaded ${urls.length} images for item ${itemName}`);
    return urls;
  } catch (error) {
    console.error('Error uploading item images:', error);
    throw error;
  }
}

/**
 * Delete all images for a specific item
 * @param {string} userId - The user's UID
 * @param {string} journeyId - The journey ID
 * @param {string} itemName - The item/product name
 * @returns {Promise<void>}
 */
export async function deleteItemImages(userId, journeyId, itemName) {
  if (!userId || !journeyId || !itemName) return;
  
  try {
    const sanitizedItemName = itemName.replace(/[^a-zA-Z0-9-_]/g, '_');
    const folderPath = `user/${userId}/journey/${journeyId}/item/${sanitizedItemName}`;
    const folderRef = ref(storage, folderPath);
    
    // List all files in the folder
    const listResult = await listAll(folderRef);
    
    // Delete all files
    const deletePromises = listResult.items.map(itemRef => deleteObject(itemRef));
    await Promise.all(deletePromises);
    
    console.log(`Deleted all images for item ${itemName} (${listResult.items.length} files)`);
  } catch (error) {
    console.error('Error deleting item images:', error);
    // Don't throw - continue even if deletion fails
  }
}

/**
 * Upload a single image for an item with specific index
 * Useful for updating individual images
 * @param {File} file - Image file
 * @param {string} userId - The user's UID
 * @param {string} journeyId - The journey ID
 * @param {string} itemName - The item/product name
 * @param {number} imageIndex - Image index (1-based)
 * @returns {Promise<string>} - Download URL
 */
export async function uploadSingleItemImage(file, userId, journeyId, itemName, imageIndex) {
  if (!file || !userId || !journeyId || !itemName || !imageIndex) {
    throw new Error('Missing required parameters for single item image upload');
  }

  try {
    const sanitizedItemName = itemName.replace(/[^a-zA-Z0-9-_]/g, '_');
    const extension = file.name.split('.').pop() || 'jpg';
    const fileName = `image-${imageIndex}.${extension}`;
    const filePath = `user/${userId}/journey/${journeyId}/item/${sanitizedItemName}/${fileName}`;
    
    const storageRef = ref(storage, filePath);
    
    // Upload the file (overwrites if exists)
    const snapshot = await uploadBytes(storageRef, file);
    
    // Get the download URL
    const downloadURL = await getDownloadURL(snapshot.ref);
    
    console.log(`Uploaded image-${imageIndex} for item ${itemName}`);
    return downloadURL;
  } catch (error) {
    console.error('Error uploading single item image:', error);
    throw error;
  }
}
